"use client";

import { useDict } from "@/components/i18n/DictProvider";
import { RichHtml } from "@/components/ui/RichHtml";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import type { Lang } from "@/lib/i18n";
import { EventProgrammeList } from "./EventProgrammeList";
import { OrganizationChips } from "./OrganizationChips";
import { InstructorCards } from "./InstructorCards";
import { KeyPoints } from "@/components/content/KeyPoints";

export function EventView({
  module: m,
  lang,
  isAuthed: _isAuthed,
  watchBase = null,
}: {
  module: ModuleDetail;
  lang: Lang;
  isAuthed: boolean;
  /** /lang/library/events/[id]/[slug] when the user may watch; else null. */
  watchBase?: string | null;
}) {
  const t = useDict();
  const ev = m.event;
  const hasAbout = !!(m.abstract || m.description || m.summary || m.takeaways?.length);

  return (
    <article className="grid gap-6 min-w-0">
      {hasAbout ? (
        <SectionCard title={t.detail.aboutEvent}>
          {m.abstract ? (
            <RichHtml
              html={m.abstract}
              className="relative pl-5 border-l-2 border-accent/70 text-[16px] md:text-[18px] leading-relaxed text-foreground/85 mb-5"
            />
          ) : null}
          <KeyPoints
            title={t.detail.keyPoints}
            summary={m.summary}
            takeaways={m.takeaways}
            className="mb-5"
          />
          {m.description ? <RichHtml html={m.description} /> : null}
        </SectionCard>
      ) : null}

      {ev?.items?.length ? (
        <EventProgrammeList items={ev.items} title={t.detail.programme} watchBase={watchBase} />
      ) : null}

      {ev?.instructors && ev.instructors.length > 0 ? (
        <SectionCard
          title={ev.instructors.length === 1 ? t.detail.instructorOne : t.detail.instructorMany}
        >
          <InstructorCards instructors={ev.instructors} />
        </SectionCard>
      ) : null}

      <OrganizationChips organizations={m.organizations} t={t} lang={lang} />
    </article>
  );
}

// Mirrors CourseView's SectionCard chrome so /events / /courses / etc. share
// the same body-section visual language.
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60">
      <h2 className="!text-[20px] md:!text-[22px] !font-semibold !leading-tight !text-foreground !mb-5">
        {title}
      </h2>
      {children}
    </section>
  );
}
